import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { useSwiper, useSwiperSlide } from 'swiper/react';

import { useGalleryData } from '../Gallery.utils';
// TODO replace with THC icons when all are available
import { ReactComponent as MinusIcon } from '../icons/minus.svg';
import { ReactComponent as PlusIcon } from '../icons/plus.svg';
import { ReactComponent as ShrinkIcon } from '../icons/shrink.svg';

import { Empty } from './Empty';
import type { GalleryImageWithTitle } from './MainGallery';

import styles from './Zoom.module.scss';

export interface ZoomEvents {
	onZoomIn?: (image: GalleryImageWithTitle) => void;
	onZoomOut?: (image: GalleryImageWithTitle) => void;
	onZoomReset?: (image: GalleryImageWithTitle) => void;
}

export interface ZoomProps {
	category: string;
	disabled?: boolean;
	events?: ZoomEvents;
}

const BUTTON_CLICK_ZOOM_STEP = 0.5;
const DOUBLE_CLICK_ZOOM_STEP = 1.1;
const MOUSE_WHEEL_ZOOM_STEP = 0.1;
const INITIAL_ZOOM = 1;
const SCALE_DEBOUNCE_UPDATE = 100;

const zoomTransformProps = {
	centerOnInit: true,
	centerZoomedOut: true,
	disablePadding: true,
	limitToBounds: true,
	maxScale: 6,
	wheel: {
		step: MOUSE_WHEEL_ZOOM_STEP,
	},
};

export const Zoom: React.FC<PropsWithChildren<ZoomProps>> = ({ category, children, disabled, events }) => {
	const { images } = useGalleryData();
	const zoom = useRef<ReactZoomPanPinchRef>(null);
	const swiper = useSwiper();
	const { isActive } = useSwiperSlide();
	const [mode, setMode] = useState<'zoomIn' | 'reset'>('zoomIn');
	const [scale, setScale] = useState<number>(INITIAL_ZOOM);

	const debouncedSetScale = useRef(
		debounce((value) => {
			setScale(value);
			setMode(value > INITIAL_ZOOM ? 'reset' : 'zoomIn');
		}, SCALE_DEBOUNCE_UPDATE),
	).current;

	const reset = useCallback(
		({ shouldTriggerEvent }: { shouldTriggerEvent: boolean }) => {
			zoom.current?.resetTransform();
			if (shouldTriggerEvent) events?.onZoomReset?.(images[swiper.activeIndex]);
		},
		[images, swiper.activeIndex],
	);

	const zoomIn = () => {
		zoom.current?.zoomIn(BUTTON_CLICK_ZOOM_STEP);
		events?.onZoomIn?.(images[swiper.activeIndex]);
	};

	const zoomOut = () => {
		zoom.current?.zoomOut(BUTTON_CLICK_ZOOM_STEP);
		events?.onZoomOut?.(images[swiper.activeIndex]);
	};

	useEffect(() => {
		swiper.allowTouchMove = scale === INITIAL_ZOOM;
	}, [scale, swiper]);

	useEffect(() => {
		zoom.current?.instance?.onChange?.((zoomInstance) => {
			debouncedSetScale(zoomInstance.state.scale);
		});
	}, [debouncedSetScale]);

	useEffect(() => {
		if (swiper.activeIndex !== null) {
			reset({ shouldTriggerEvent: false });
		}
	}, [swiper.activeIndex, reset]);

	const isZoomedIn = scale > INITIAL_ZOOM;

	return (
		<div
			className={cx(styles.container, { [styles.zoomed]: isZoomedIn, [styles.empty]: !!category })}
			data-testid="zoom-wrapper"
		>
			{isZoomedIn && (
				<div className={styles.info} data-testid="zoom-factor">
					{Math.round(scale * 100)}%
				</div>
			)}
			<TransformWrapper
				ref={zoom}
				disabled={disabled}
				doubleClick={{ mode, step: DOUBLE_CLICK_ZOOM_STEP }}
				panning={{
					disabled: !isZoomedIn,
				}}
				{...zoomTransformProps}
			>
				<TransformComponent
					contentClass={cx(styles.content, { [styles.disabled]: disabled })}
					wrapperClass={styles.wrapper}
				>
					{children}
				</TransformComponent>
			</TransformWrapper>
			{isActive && !disabled && (
				<div className={styles.controls}>
					{isZoomedIn && (
						<button
							aria-label="Reset Zoom"
							className={styles.zoomReset}
							onClick={() => reset({ shouldTriggerEvent: true })}
						>
							<ShrinkIcon />
						</button>
					)}
					<div className={styles.zoom}>
						<button aria-label="Zoom In" onClick={zoomIn}>
							<PlusIcon />
						</button>
						<div className={styles.divider} />
						<button aria-label="Zoom Out" onClick={zoomOut}>
							<MinusIcon />
						</button>
					</div>
				</div>
			)}
			{category && <Empty category={category} />}
		</div>
	);
};
